import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../utils/api';
import Navbar from '../components/Navbar';
import {
    ArrowLeft,
    ChatCircleText,
    CheckCircle,
    Clock,
    GlobeHemisphereWest,
    Heart,
    MapPin,
    MusicNotes,
    ShieldCheck,
    User,
    UserPlus,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const publicProfileTexts = {
    de: {
        communityProfile: 'WORLDofRADIO Community-Profil',
        loading: 'Profil wird geladen...',
        notFound: 'Profil nicht gefunden.',
        back: 'Zurück',
        publicIdentity: 'Öffentliche Identität',
        privateHint: 'E-Mail und echter Name bleiben privat.',
        favoriteGenres: 'Lieblingsgenres',
        noGenres: 'Noch keine Genres hinzugefügt.',
        country: 'Land',
        noCountry: 'Noch kein Land angegeben.',
        bio: 'Bio',
        noBio: 'Noch keine Bio vorhanden.',
        verified: 'VERIFIZIERT',
        admin: 'ADMIN',
        favorites: 'Favoriten',
        comments: 'Kommentare',
        friends: 'Freunde',
        following: 'Folgt',
        addFriend: 'Freund hinzufügen',
        requestSent: 'Anfrage gesendet',
        incomingRequest: 'Anfrage erhalten',
        alreadyFriends: 'Ihr seid Freunde',
        startChat: 'Chat starten',
        chatSoon: 'Chat startet im nächsten Schritt',
        sendRequestError: 'Freundschaftsanfrage konnte nicht gesendet werden.',
        languageSettings: 'Spracheinstellungen',
        writes: 'Schreibt',
        reads: 'Liest',
    },

    en: {
        communityProfile: 'WORLDofRADIO Community Profile',
        loading: 'Loading profile...',
        notFound: 'Profile not found.',
        back: 'Back',
        publicIdentity: 'Public Identity',
        privateHint: 'Email and real name stay private.',
        favoriteGenres: 'Favorite Genres',
        noGenres: 'No genres added yet.',
        country: 'Country',
        noCountry: 'No country provided yet.',
        bio: 'Bio',
        noBio: 'No bio yet.',
        verified: 'VERIFIED',
        admin: 'ADMIN',
        favorites: 'Favorites',
        comments: 'Comments',
        friends: 'Friends',
        following: 'Following',
        addFriend: 'Add friend',
        requestSent: 'Request sent',
        incomingRequest: 'Request received',
        alreadyFriends: 'You are friends',
        startChat: 'Start chat',
        chatSoon: 'Chat starts in the next step',
        sendRequestError: 'Friend request could not be sent.',
        languageSettings: 'Language Settings',
        writes: 'Writes',
        reads: 'Reads',
    },

    no: {
        communityProfile: 'WORLDofRADIO fellesskapsprofil',
        loading: 'Laster profil...',
        notFound: 'Profilen ble ikke funnet.',
        back: 'Tilbake',
        publicIdentity: 'Offentlig identitet',
        privateHint: 'E-post og ekte navn forblir private.',
        favoriteGenres: 'Favorittsjangre',
        noGenres: 'Ingen sjangre lagt til ennå.',
        country: 'Land',
        noCountry: 'Ingen land angitt ennå.',
        bio: 'Bio',
        noBio: 'Ingen bio ennå.',
        verified: 'VERIFISERT',
        admin: 'ADMIN',
        favorites: 'Favoritter',
        comments: 'Kommentarer',
        friends: 'Venner',
        following: 'Følger',
        addFriend: 'Legg til venn',
        requestSent: 'Forespørsel sendt',
        incomingRequest: 'Forespørsel mottatt',
        alreadyFriends: 'Dere er venner',
        startChat: 'Start chat',
        chatSoon: 'Chat starter i neste steg',
        sendRequestError: 'Kunne ikke sende venneforespørsel.',
        languageSettings: 'Språkinnstillinger',
        writes: 'Skriver',
        reads: 'Leser',
    },

    ko: {
        communityProfile: 'WORLDofRADIO 커뮤니티 프로필',
        loading: '프로필을 불러오는 중...',
        notFound: '프로필을 찾을 수 없습니다.',
        back: '뒤로',
        publicIdentity: '공개 신원',
        privateHint: '이메일과 실명은 비공개로 유지됩니다.',
        favoriteGenres: '좋아하는 장르',
        noGenres: '아직 추가된 장르가 없습니다.',
        country: '국가',
        noCountry: '아직 국가 정보가 없습니다.',
        bio: '소개',
        noBio: '아직 소개가 없습니다.',
        verified: '인증됨',
        admin: '관리자',
        favorites: '즐겨찾기',
        comments: '댓글',
        friends: '친구',
        following: '팔로잉',
        addFriend: '친구 추가',
        requestSent: '요청 전송됨',
        incomingRequest: '요청 받음',
        alreadyFriends: '친구입니다',
        startChat: '채팅 시작',
        chatSoon: '채팅은 다음 단계에서 시작됩니다',
        sendRequestError: '친구 요청을 보낼 수 없습니다.',
        languageSettings: '언어 설정',
        writes: '작성',
        reads: '읽기',
    },

    fi: {
        communityProfile: 'WORLDofRADIO-yhteisöprofiili',
        loading: 'Ladataan profiilia...',
        notFound: 'Profiilia ei löytynyt.',
        back: 'Takaisin',
        publicIdentity: 'Julkinen identiteetti',
        privateHint: 'Sähköposti ja oikea nimi pysyvät yksityisinä.',
        favoriteGenres: 'Suosikkigenret',
        noGenres: 'Genrejä ei ole vielä lisätty.',
        country: 'Maa',
        noCountry: 'Maata ei ole vielä annettu.',
        bio: 'Bio',
        noBio: 'Bioa ei ole vielä.',
        verified: 'VAHVISTETTU',
        admin: 'YLLÄPITO',
        favorites: 'Suosikit',
        comments: 'Kommentit',
        friends: 'Ystävät',
        following: 'Seuratut',
        addFriend: 'Lisää ystävä',
        requestSent: 'Pyyntö lähetetty',
        incomingRequest: 'Pyyntö vastaanotettu',
        alreadyFriends: 'Olette ystäviä',
        startChat: 'Aloita chat',
        chatSoon: 'Chat alkaa seuraavassa vaiheessa',
        sendRequestError: 'Ystäväpyyntöä ei voitu lähettää.',
        languageSettings: 'Kieliasetukset',
        writes: 'Kirjoittaa',
        reads: 'Lukee',
    },
};

const PublicProfile = () => {
    const { username } = useParams();
    const navigate = useNavigate();
    const { language } = useLanguage();
    const text = publicProfileTexts[language] || publicProfileTexts.en;

    const [profile, setProfile] = useState(null);
    const [stats, setStats] = useState({
        favorites: 0,
        comments: 0,
        followers: 0,
        following: 0,
    });
    const [friendshipStatus, setFriendshipStatus] = useState('none');
    const [loading, setLoading] = useState(true);
    const [requestLoading, setRequestLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [username]);

    const loadProfile = async () => {
        if (!username) return;

        try {
            setLoading(true);
            setError('');

            const response = await api.getUserProfile(username);
            const data = response.data || {};

            const userData = data.user || data;

            setProfile(userData);
            setStats(
                data.stats || userData.stats || {
                    favorites: 0,
                    comments: 0,
                    followers: 0,
                    following: 0,
                }
            );

            setFriendshipStatus(
                data.friendship_status ||
                userData.friendship_status ||
                userData.relationship_status ||
                'none'
            );
        } catch (err) {
            console.error('Failed to load public profile:', err);
            setProfile(null);
            setError(text.notFound);
        } finally {
            setLoading(false);
        }
    };

    const getMusicGenres = () => {
        if (!profile) return [];

        if (Array.isArray(profile.music_genres)) {
            return profile.music_genres.filter(Boolean);
        }

        if (profile.music_genres) {
            return [profile.music_genres];
        }

        return [];
    };

    const handleSendFriendRequest = async () => {
        if (!profile?.user_id) return;

        try {
            setRequestLoading(true);
            setError('');

            await api.sendFriendRequest(profile.user_id);
            setFriendshipStatus('outgoing_pending');
        } catch (err) {
            console.error('Failed to send friend request:', err);
            setError(text.sendRequestError);
        } finally {
            setRequestLoading(false);
        }
    };

    const handleChatClick = () => {
        // Der echte Chat-Routenstart kommt im nächsten Schritt.
        alert(text.chatSoon);
    };

    const renderFriendButton = () => {
        if (friendshipStatus === 'friends') {
            return (
                <Button
                    type="button"
                    onClick={handleChatClick}
                    className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full flex items-center gap-2"
                >
                    <ChatCircleText size={18} weight="bold" />
                    {text.startChat}
                </Button>
            );
        }

        if (friendshipStatus === 'outgoing_pending') {
            return (
                <Button
                    type="button"
                    disabled
                    className="bg-white/5 border border-white/10 text-gray-400 font-bold rounded-full flex items-center gap-2 disabled:opacity-100"
                >
                    <Clock size={18} />
                    {text.requestSent}
                </Button>
            );
        }

        if (friendshipStatus === 'incoming_pending') {
            return (
                <Button
                    type="button"
                    onClick={() => navigate('/notifications')}
                    className="bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 font-bold rounded-full flex items-center gap-2"
                >
                    <UserPlus size={18} />
                    {text.incomingRequest}
                </Button>
            );
        }

        return (
            <Button
                type="button"
                onClick={handleSendFriendRequest}
                disabled={requestLoading}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full flex items-center gap-2 disabled:opacity-60"
            >
                <UserPlus size={18} weight="bold" />
                {requestLoading ? text.loading : text.addFriend}
            </Button>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] pb-24">
                <Navbar />
                <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
                    <div className="rounded-3xl border border-white/10 bg-black/60 p-10 text-gray-400">
                        {text.loading}
                    </div>
                </main>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="min-h-screen bg-[#050505] pb-24">
                <Navbar />
                <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors mb-6"
                    >
                        <ArrowLeft size={18} />
                        {text.back}
                    </button>

                    <div className="rounded-3xl border border-white/10 bg-black/60 p-10 text-gray-400">
                        {error || text.notFound}
                    </div>
                </main>
            </div>
        );
    }

    const publicUsername = profile.username || username;
    const avatarSource = profile.avatar || profile.avatar_url || '';
    const fallbackLetter = publicUsername.charAt(0).toUpperCase();
    const musicGenres = getMusicGenres();
    const preferredLanguage = profile.preferred_language || 'de';
    const originalLanguage = profile.original_language || 'de';

    return (
        <div className="min-h-screen bg-[#050505] pb-24">
            <Navbar />

            <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-cyan-400 transition-colors mb-6"
                >
                    <ArrowLeft size={18} />
                    {text.back}
                </button>

                {error && (
                    <div className="mb-6 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
                        {error}
                    </div>
                )}

                <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8 lg:p-10">
                    <div className="flex flex-col items-center mb-10">
                        <Avatar className="w-32 h-32 border-4 border-cyan-500/50 mb-5">
                            <AvatarImage src={avatarSource} alt={publicUsername} />
                            <AvatarFallback className="bg-cyan-500/20 text-cyan-400 font-bold text-4xl">
                                {fallbackLetter}
                            </AvatarFallback>
                        </Avatar>

                        <h1
                            className="text-3xl tracking-tighter font-black text-white mb-1"
                            style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                            @{publicUsername}
                        </h1>

                        <p className="text-gray-400 text-center">{text.communityProfile}</p>

                        <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
                            {profile.role === 'admin' && (
                                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded-full">
                                    {text.admin}
                                </span>
                            )}

                            {profile.is_verified && (
                                <span className="px-3 py-1 bg-green-500/15 text-green-400 text-xs font-bold rounded-full flex items-center gap-1">
                                    <ShieldCheck size={14} />
                                    {text.verified}
                                </span>
                            )}

                            {friendshipStatus === 'friends' && (
                                <span className="px-3 py-1 bg-cyan-500/10 text-cyan-300 text-xs font-bold rounded-full flex items-center gap-1 border border-cyan-500/20">
                                    <CheckCircle size={14} weight="fill" />
                                    {text.alreadyFriends}
                                </span>
                            )}
                        </div>

                        <div className="mt-6">{renderFriendButton()}</div>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mb-10">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-cyan-400">
                                {stats.favorites || 0}
                            </div>
                            <div className="text-sm text-gray-400">{text.favorites}</div>
                        </div>

                        <div className="text-center">
                            <div className="text-2xl font-bold text-cyan-400">
                                {stats.comments || 0}
                            </div>
                            <div className="text-sm text-gray-400">{text.comments}</div>
                        </div>

                        <div className="text-center">
                            <div className="text-2xl font-bold text-cyan-400">
                                {stats.followers || 0}
                            </div>
                            <div className="text-sm text-gray-400">{text.friends}</div>
                        </div>

                        <div className="text-center">
                            <div className="text-2xl font-bold text-cyan-400">
                                {stats.following || 0}
                            </div>
                            <div className="text-sm text-gray-400">{text.following}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                            <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                                <User size={20} />
                                {text.publicIdentity}
                            </div>
                            <p className="text-gray-300">@{publicUsername}</p>
                            <p className="text-xs text-gray-500 mt-2">{text.privateHint}</p>
                        </div>

                        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                            <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                                <GlobeHemisphereWest size={20} />
                                {text.languageSettings}
                            </div>
                            <p className="text-gray-300">
                                {text.writes}: {originalLanguage.toUpperCase()}
                            </p>
                            <p className="text-gray-300">
                                {text.reads}: {preferredLanguage.toUpperCase()}
                            </p>
                        </div>

                        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                            <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                                <MusicNotes size={20} />
                                {text.favoriteGenres}
                            </div>

                            {musicGenres.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {musicGenres.map((genre) => (
                                        <span
                                            key={genre}
                                            className="px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-300 text-sm border border-cyan-500/20"
                                        >
                                            {genre}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500">{text.noGenres}</p>
                            )}
                        </div>

                        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                            <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                                <GlobeHemisphereWest size={20} />
                                {text.country}
                            </div>

                            {profile.country ? (
                                <div className="flex items-center gap-2 text-gray-300">
                                    <MapPin size={18} className="text-cyan-400" />
                                    <span>{profile.country}</span>
                                </div>
                            ) : (
                                <p className="text-gray-500">{text.noCountry}</p>
                            )}
                        </div>
                    </div>

                    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                        <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                            <MusicNotes size={20} />
                            {text.bio}
                        </div>

                        {profile.bio ? (
                            <p className="text-gray-300 leading-relaxed">{profile.bio}</p>
                        ) : (
                            <p className="text-gray-500">{text.noBio}</p>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default PublicProfile;